import { Link } from "react-router-dom";
import { useState, useEffect } from "react";
import { Share2, Globe, Mail, Smartphone, ChevronRight, Target, Zap, Layers } from "lucide-react";

// Navigation Component
const Navigation = () => {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "glass py-3" : "bg-transparent py-5"
      }`}
    >
      <div className="container mx-auto px-4 lg:px-8 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2">
          <img 
            src="https://www.machinerymarketed.com/app-logo.png" 
            alt="MachineryMarketed Logo" 
            className="h-10 w-10"
          />
          <span className="font-bold text-xl tracking-tight" style={{ fontFamily: "'Smooch Sans', sans-serif", color: "var(--text-100)" }}>
            MACHINERYMARKETED
          </span>
        </Link>
        <Link to="/" className="btn-outline text-sm">Back to Home</Link>
      </div>
    </nav>
  );
};

// Channel Card Component
const ChannelCard = ({ icon: Icon, title, description, color }) => (
  <div 
    className="card p-6 h-full"
    style={{ 
      background: "var(--background-200)", 
      borderColor: `${color}30`,
      borderTopWidth: "3px",
      borderTopColor: color
    }}
  >
    <div 
      className="w-14 h-14 rounded-2xl flex items-center justify-center mb-5"
      style={{ background: `${color}20` }}
    >
      <Icon size={28} style={{ color: color }} />
    </div>
    <h3 
      className="text-xl font-bold mb-3"
      style={{ fontFamily: "'Smooch Sans', sans-serif", color: "var(--text-100)" }}
    >
      {title}
    </h3>
    <p style={{ color: "var(--text-200)", lineHeight: "1.6" }}>{description}</p>
  </div>
);

// Benefit Item Component
const BenefitItem = ({ icon: Icon, title, description }) => (
  <div className="flex gap-4">
    <div 
      className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
      style={{ background: "var(--primary-100)" }}
    >
      <Icon size={24} style={{ color: "white" }} />
    </div>
    <div>
      <h4 
        className="text-lg font-bold mb-1"
        style={{ fontFamily: "'Smooch Sans', sans-serif", color: "var(--text-100)" }}
      >
        {title}
      </h4>
      <p style={{ color: "var(--text-200)", lineHeight: "1.6" }}>{description}</p>
    </div>
  </div>
);

// Main Marketing Distribution Page Component
const MarketingDistributionPage = () => {
  const channels = [
    {
      icon: Smartphone,
      title: "Social Media",
      description: "Your dealership's social media pages — Facebook, Instagram, LinkedIn — updated instantly with professional listings.",
      color: "#0f5fff"
    },
    {
      icon: Globe,
      title: "Your Website",
      description: "Seamless integration with your dealership website. Listings appear automatically, styled to match your brand.",
      color: "#0049db"
    },
    {
      icon: Share2,
      title: "MachineryMarketed Channels",
      description: "Exposure through MachineryMarketed's own social media and website, reaching thousands of potential buyers.",
      color: "#0038a8"
    },
    {
      icon: Mail,
      title: "Email Campaigns",
      description: "Custom-built email databases and targeted campaigns, helping you reach buyers directly with the right machines.",
      color: "#0f5fff"
    }
  ];

  return (
    <div style={{ background: "var(--background-100)", minHeight: "100vh" }}>
      <Navigation />
      
      <main className="pt-24">
        {/* Hero Section */}
        <section className="py-20 relative overflow-hidden">
          <div 
            className="absolute inset-0 z-0"
            style={{
              background: "radial-gradient(ellipse at center, rgba(15, 95, 255, 0.1) 0%, transparent 70%)"
            }}
          />
          
          <div className="container mx-auto px-4 lg:px-8 relative z-10">
            <div className="max-w-4xl mx-auto text-center">
              <span 
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold mb-6"
                style={{ background: "var(--primary-100)", color: "white" }}
              >
                <Share2 size={18} />
                Multi-Channel Marketing
              </span>
              <h1 
                className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold mb-8 px-2"
                style={{ fontFamily: "'Smooch Sans', sans-serif", color: "var(--text-100)", wordWrap: "break-word" }}
              >
                Marketing <span className="text-gradient">Distribution</span>
              </h1>
              <p 
                className="text-xl max-w-3xl mx-auto"
                style={{ color: "var(--text-200)" }}
              >
                MachineryMarketed is designed to get your machines in front of the market{" "}
                <span style={{ color: "var(--primary-300)", fontWeight: 600 }}>quickly</span>,{" "}
                <span style={{ color: "var(--primary-300)", fontWeight: 600 }}>consistently</span>, and{" "}
                <span style={{ color: "var(--primary-300)", fontWeight: 600 }}>professionally</span>.
              </p>
            </div>
          </div>
        </section>

        {/* How It Works Section */}
        <section className="py-16">
          <div className="container mx-auto px-4 lg:px-8">
            <div className="max-w-4xl mx-auto">
              <div 
                className="card p-10"
                style={{ 
                  background: "var(--background-200)", 
                  borderColor: "rgba(15, 95, 255, 0.2)"
                }}
              >
                <p 
                  className="text-xl text-center"
                  style={{ color: "var(--text-100)", lineHeight: "1.8" }}
                >
                  Once a machine is ready for sale, the same appraisal data and photos are used to 
                  create a polished advertisement — <span style={{ color: "var(--primary-300)", fontWeight: 600 }}>no rework, no double handling</span>. 
                  From there, MachineryMarketed allows you to push that listing across the channels 
                  that matter most.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Distribution Channels */}
        <section className="py-20" style={{ background: "var(--background-200)" }}>
          <div className="container mx-auto px-4 lg:px-8">
            <div className="text-center mb-16">
              <h2 
                className="text-3xl md:text-4xl font-bold mb-4"
                style={{ fontFamily: "'Smooch Sans', sans-serif", color: "var(--text-100)" }}
              >
                Distribution Channels
              </h2>
              <p style={{ color: "var(--text-200)" }}>
                Your machines can be distributed across multiple platforms — all from one place
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
              {channels.map((channel, index) => (
                <ChannelCard key={index} {...channel} />
              ))}
            </div>
          </div>
        </section>

        {/* Key Advantages */}
        <section className="py-20">
          <div className="container mx-auto px-4 lg:px-8">
            <div className="max-w-4xl mx-auto">
              <h2 
                className="text-3xl md:text-4xl font-bold text-center mb-12"
                style={{ fontFamily: "'Smooch Sans', sans-serif", color: "var(--text-100)" }}
              >
                The Key Advantage: <span className="text-gradient">Control</span>
              </h2>
              
              <div className="grid md:grid-cols-3 gap-8 mb-12">
                <div className="text-center">
                  <Target size={40} style={{ color: "var(--primary-300)", margin: "0 auto 16px" }} />
                  <h3 
                    className="text-xl font-bold mb-2"
                    style={{ fontFamily: "'Smooch Sans', sans-serif", color: "var(--text-100)" }}
                  >
                    You Choose
                  </h3>
                  <p style={{ color: "var(--text-200)" }}>
                    Where your machines are published, how they're presented, and when they go live.
                  </p>
                </div>
                <div className="text-center">
                  <Layers size={40} style={{ color: "var(--primary-300)", margin: "0 auto 16px" }} />
                  <h3 
                    className="text-xl font-bold mb-2"
                    style={{ fontFamily: "'Smooch Sans', sans-serif", color: "var(--text-100)" }}
                  >
                    Consistent Everywhere
                  </h3>
                  <p style={{ color: "var(--text-200)" }}>
                    Images, descriptions, and key details stay consistent across all channels.
                  </p>
                </div>
                <div className="text-center">
                  <Zap size={40} style={{ color: "var(--primary-300)", margin: "0 auto 16px" }} />
                  <h3 
                    className="text-xl font-bold mb-2"
                    style={{ fontFamily: "'Smooch Sans', sans-serif", color: "var(--text-100)" }}
                  >
                    Faster Results
                  </h3>
                  <p style={{ color: "var(--text-200)" }}>
                    Less time spent manually listing and relisting across multiple platforms.
                  </p>
                </div>
              </div>

              <div 
                className="card p-8 text-center"
                style={{ 
                  background: "var(--background-200)", 
                  borderColor: "rgba(15, 95, 255, 0.3)",
                  borderLeftWidth: "4px",
                  borderLeftColor: "var(--primary-300)"
                }}
              >
                <p 
                  className="text-lg"
                  style={{ color: "var(--text-200)", lineHeight: "1.8" }}
                >
                  The result is <span style={{ color: "var(--primary-300)", fontWeight: 600 }}>faster exposure</span>,{" "}
                  <span style={{ color: "var(--primary-300)", fontWeight: 600 }}>stronger presentation</span>, and{" "}
                  <span style={{ color: "var(--primary-300)", fontWeight: 600 }}>less time</span> spent manually listing 
                  and relisting machines across multiple channels.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Custom Setups */}
        <section className="py-16" style={{ background: "var(--background-200)" }}>
          <div className="container mx-auto px-4 lg:px-8">
            <div className="max-w-4xl mx-auto">
              <div className="grid md:grid-cols-2 gap-8 items-center">
                <div>
                  <h2 
                    className="text-3xl font-bold mb-4"
                    style={{ fontFamily: "'Smooch Sans', sans-serif", color: "var(--text-100)" }}
                  >
                    Custom Marketing Setups
                  </h2>
                  <p 
                    className="mb-6"
                    style={{ color: "var(--text-200)", lineHeight: "1.7" }}
                  >
                    For dealerships wanting to go further, we can also support custom-built email 
                    databases and targeted email campaigns, helping you reach buyers directly with 
                    the right machines at the right time.
                  </p>
                  <p style={{ color: "var(--text-300)" }}>
                    If you'd like to know more about specific distribution options, platforms, or 
                    custom marketing setups, we're happy to talk.
                  </p>
                </div>
                <div 
                  className="card p-8 text-center"
                  style={{ 
                    background: "var(--background-300)", 
                    borderColor: "rgba(15, 95, 255, 0.2)"
                  }}
                >
                  <Mail size={48} style={{ color: "var(--primary-300)", margin: "0 auto 16px" }} />
                  <h3 
                    className="text-xl font-bold mb-3"
                    style={{ fontFamily: "'Smooch Sans', sans-serif", color: "var(--text-100)" }}
                  >
                    Ready to Discuss?
                  </h3>
                  <p className="mb-6" style={{ color: "var(--text-200)" }}>
                    Let's talk about what distribution looks like for your dealership.
                  </p>
                  <Link to="/#contact" className="btn-primary inline-flex items-center gap-2">
                    Contact Us <ChevronRight size={18} />
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="py-12" style={{ background: "var(--background-200)", borderTop: "1px solid rgba(176, 176, 176, 0.1)" }}>
        <div className="container mx-auto px-4 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-3">
              <img 
                src="https://www.machinerymarketed.com/app-logo.png" 
                alt="MachineryMarketed Logo" 
                className="h-10 w-10"
              />
              <span className="font-bold text-lg" style={{ fontFamily: "'Smooch Sans', sans-serif", color: "var(--text-100)" }}>
                MACHINERYMARKETED
              </span>
            </div>

            <div className="flex items-center gap-6">
              <Link to="/about" className="footer-link text-sm">About</Link>
              <Link to="/privacy" className="footer-link text-sm">Privacy</Link>
              <Link to="/terms" className="footer-link text-sm">Terms</Link>
              <Link to="/cookies" className="footer-link text-sm">Cookies</Link>
              <Link to="/faq" className="footer-link text-sm">FAQ</Link>
            </div>

            <p className="text-sm" style={{ color: "var(--text-300)" }}>
              © 2026 MachineryMarketed. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default MarketingDistributionPage;
